import { useEffect, useId, useRef, useState } from 'react';
import SectionWrapper from '../../SectionWrapper';
import { Sanity$EmbeddedHubspotForm } from '@otovo/shared/types/sanityTypes';
import Script from 'next/script';

/**
 * Component that renders a HubSpot form using the provided formId
 * and the HubSpot account ID from environment variables
 * Supports multiple instances on the same page
 */
const EmbeddedHubspotForm = ({
  form,
  onFormLoaded,
}: {
  form: Sanity$EmbeddedHubspotForm;
  onFormLoaded?: () => void;
}) => {
  const formContainerRef = useRef<HTMLDivElement>(null);
  const hubspotPortalId = process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID;
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const fallBackId = useId();

  const instanceId = form._key + fallBackId;
  const uniqueContainerId =
    `hubspot-form-${form.formId}-${instanceId || ''}`.replace(
      /[^a-zA-Z0-9-]/g,
      '-',
    );

  useEffect(() => {
    // Check if the HubSpot form script is already loaded
    if (
      window.hbspt &&
      !formContainerRef.current.querySelectorAll('iframe').length
    ) {
      window.hbspt.forms.create({
        portalId: hubspotPortalId,
        formInstanceId: instanceId,
        formId: form.formId,
        target: `#${uniqueContainerId}`,
        region: 'eu1',
        onFormLoaded: onFormLoaded || (() => {}),
      });
    }
  }, [
    scriptLoaded,
    form.formId,
    hubspotPortalId,
    instanceId,
    uniqueContainerId,
    onFormLoaded,
  ]);

  return (
    <SectionWrapper>
      <Script
        type="text/javascript"
        src="https://js.hsforms.net/forms/embed/v2.js"
        onReady={() => {
          setScriptLoaded(true);
        }}
        onLoad={() => {
          setScriptLoaded(true);
        }}
      />

      <div
        className="col-span-12 flex flex-col items-center justify-center"
        key={fallBackId}
      >
        <div className="h-full w-full">
          <div
            id={uniqueContainerId}
            ref={formContainerRef}
            className="hubspot-form-container"
          >
            {!scriptLoaded && (
              <div className="loading-placeholder">Loading form...</div>
            )}
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export default EmbeddedHubspotForm;
